import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { RelatedLocations } from './RelatedLocations';
import { LocationFAQ } from './LocationFAQ';
import { SEO } from './SEO';
import { 
  Server, Zap, Shield, Globe, 
  CheckCircle, Network, Wifi,
  Clock, MapPin, TrendingUp, Database,
  Anchor, Palmtree, Cable, Cpu,
  ArrowRight, ChevronDown, Layers, Lock
} from 'lucide-react';
import { Button } from './ui/button';
import { PricingSection } from './PricingSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { smoothScrollToWithOffset } from '../lib/scroll';

export const MiamiPage = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);
  const [openAccordion, setOpenAccordion] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToPricing = () => smoothScrollToWithOffset('pricing', 100);

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO page="miami" />
      <Header />
      
      {/* Hero Section - Tropical Teal/Cyan gradient */}
      <section className="relative min-h-screen flex items-center overflow-hidden py-20 sm:py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900 via-cyan-800 to-teal-900">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-cyan-400 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-teal-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
            <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-emerald-400 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '3s' }}></div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-sm font-semibold mb-6">
                <MapPin className="w-4 h-4" />
                <span>{t('miami.hero.badge')}</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-black mb-6 leading-tight">
                {t('miami.hero.title')}
              </h1>
              
              <p className="text-xl md:text-2xl text-cyan-100 mb-8 max-w-xl font-light leading-relaxed">
                {t('miami.hero.subtitle')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={scrollToPricing}
                  className="bg-white text-teal-900 hover:bg-cyan-50 font-bold px-8 py-6 text-lg rounded-xl shadow-2xl"
                >
                  {t('miami.hero.getStarted')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  onClick={scrollToPricing}
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold px-8 py-6 text-lg rounded-xl"
                >
                  {t('miami.hero.viewPlans')}
                </Button>
              </div>
            </div>
            
            {/* Right - Floating Stats Cards */}
            <div className="relative hidden lg:block">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src="/images/locations/miami-skyline.jpg" 
                  alt="Miami Skyline" loading="lazy"
                  className="w-full h-96 object-cover" />
              </div>
              
              {/* Floating cards over image */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <Link
                      to={(language === 'ru' ? '/ru' : '') + '/wiki/shared-1gbps-port-explained'}
                      className="text-xl font-black text-gray-900 hover:text-teal-700 transition-colors"
                      title={language === 'ru' ? 'Что значит общий 1 Гбит/с?' : 'What does shared 1 Gbps mean?'}
                    >
                      1Gbps
                    </Link>
                    <div className="text-xs text-gray-500">{t('miami.hero.portSpeed')}</div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <div className="text-xl font-black text-gray-900">99.9%</div>
                    <div className="text-xs text-gray-500">Uptime SLA</div>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 -right-8 bg-white rounded-xl p-4 shadow-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-xl font-black text-gray-900">&lt;8ms</div>
                    <div className="text-xs text-gray-500">{t('miami.hero.latency')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Stats Row */}
          <div className="grid grid-cols-3 gap-4 mt-12 lg:hidden">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-center">
              <Link
                to={(language === 'ru' ? '/ru' : '') + '/wiki/shared-1gbps-port-explained'}
                className="text-2xl font-black text-white hover:text-cyan-200 transition-colors block"
                title={language === 'ru' ? 'Что значит общий 1 Гбит/с?' : 'What does shared 1 Gbps mean?'}
              >
                1Gbps
              </Link>
              <div className="text-xs text-cyan-200">{t('miami.hero.portSpeed')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-black text-white">99.9%</div>
              <div className="text-xs text-cyan-200">Uptime</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-black text-white">&lt;8ms</div>
              <div className="text-xs text-cyan-200">{t('miami.hero.latency')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Gateway to Americas - Horizontal cards */}
      <section className="py-20 bg-gradient-to-b from-white to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              {t('miami.gateway.title')}
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('miami.gateway.subtitle')}
            </p>
          </div>

          <div className="space-y-6">
            {t('miami.gateway.items').map((item, index) => {
              const icons = [Globe, Anchor, Cable, Network];
              const IconComponent = icons[index] || Globe;
              const colors = [
                'from-teal-500 to-cyan-600',
                'from-cyan-500 to-blue-600', 
                'from-emerald-500 to-teal-600',
                'from-blue-500 to-indigo-600'
              ];
              
              return (
                <div key={index} className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1">
                  <div className="flex flex-col sm:flex-row items-start gap-6">
                    <div className={"flex-shrink-0 w-16 h-16 bg-gradient-to-br " + colors[index] + " rounded-2xl flex items-center justify-center shadow-lg"}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Break - Miami Beach */}
      <section className="relative h-72 sm:h-96 overflow-hidden">
        <img src="/images/locations/miami-beach.jpg" 
          alt="Miami Beach" loading="lazy"
          className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute bottom-8 left-8 text-white">
          <p className="text-2xl sm:text-3xl font-bold">{t('miami.imageBreak.text')}</p>
        </div>
      </section>

      {/* Tabbed Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              {t('miami.useCases.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('miami.useCases.subtitle')}
            </p>
          </div>

          {/* Tab Buttons */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {t('miami.useCases.tabs').map((tab, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={"px-6 py-3 rounded-full font-semibold transition-all text-sm sm:text-base " + 
                  (activeTab === index 
                    ? 'bg-teal-600 text-white shadow-lg' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-3xl p-8 sm:p-12 border border-teal-100">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  {t('miami.useCases.tabs')[activeTab].title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                  {t('miami.useCases.tabs')[activeTab].desc}
                </p>
                <ul className="space-y-3">
                  {t('miami.useCases.tabs')[activeTab].features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex justify-center">
                <div className="w-full max-w-sm">
                  {[Server, Database, Shield, Layers].map((Icon, i) => (
                    activeTab === i && (
                      <div key={i} className="bg-white rounded-2xl p-8 shadow-xl text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                          <Icon className="w-10 h-10 text-white" />
                        </div>
                        <div className="text-4xl font-black text-gray-900 mb-2">{t('miami.useCases.tabs')[activeTab].stat}</div>
                        <div className="text-gray-500">{t('miami.useCases.tabs')[activeTab].statLabel}</div>
                      </div>
                    )
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related VPS Locations */}
      <RelatedLocations currentLocation="vps-miami" />

      {/* Pricing */}
      <div id="pricing">
        <PricingSection 
          hideHeader={false}
          title={t('miami.pricing.title')}
          subtitle={t('miami.pricing.subtitle')}
        />
      </div>

      <PaymentMethodsSection transparentBg={false} />

      {/* Technical Accordion Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              {t('miami.technical.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('miami.technical.subtitle')}
            </p>
          </div>

          <div className="space-y-4">
            {t('miami.technical.items').map((item, index) => {
              const icons = [Cpu, Network, Shield, Lock, Database, Wifi];
              const IconComponent = icons[index] || Server;
              
              return (
                <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-5 h-5 text-teal-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                    </div>
                    <ChevronDown className={"w-5 h-5 text-gray-400 transition-transform " + (openAccordion === index ? 'rotate-180' : '')} />
                  </button>
                  {openAccordion === index && (
                    <div className="px-6 pb-6 pt-0">
                      <div className="pl-14 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                        {item.desc}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Location Details with Images */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src="/images/locations/miami-downtown.jpg" 
                  alt="Miami Downtown Brickell" loading="lazy"
                  className="w-full h-80 object-cover" />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img src="/images/locations/miami-bridge.jpg" 
                    alt="Miami Infrastructure" loading="lazy"
                    className="w-full h-48 object-cover" />
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img src="/images/locations/320px-Flag_of_the_United_States.svg.png" 
                    alt="USA Flag" loading="lazy"
                    className="w-full h-48 object-cover" />
                </div>
              </div>
            </div>
            
            <div>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
                {t('miami.location.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t('miami.location.description')}
              </p>
              <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg mb-6">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-teal-600" />
                  {t('miami.location.subtitle')}
                </h3>
                <p className="text-gray-700">{t('miami.location.details')}</p>
              </div>
              <ul className="space-y-3">
                {t('miami.location.benefits').map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900 via-cyan-800 to-teal-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
            {t('miami.cta.title')}
          </h2>
          <p className="text-xl md:text-2xl text-cyan-100 mb-12 max-w-3xl mx-auto">
            {t('miami.cta.subtitle')}
          </p>
          <Button 
            onClick={scrollToPricing}
            className="bg-white text-teal-900 hover:bg-cyan-50 font-bold px-16 py-8 text-xl rounded-full shadow-2xl transform hover:scale-105 transition-all"
          >
            {t('miami.cta.button')}
            <ArrowRight className="w-7 h-7 ml-3" />
          </Button>
        </div>
      </section>

      <LocationFAQ location="vps-miami" />
      <Footer />
    </div>
  );
};
